import type { UpdateChunkBboxRequest } from '../types';

/**
 * The four inputs behind "add a field" (OMA-630): what to call it, what it
 * says, what colour to tint its highlight, and the region it sits in.
 *
 * The first three are typed into a dialog; the fourth is a drag on the document
 * behind it. Held together so validation can answer "what is still missing"
 * across both halves at once.
 */
export interface ManualFieldDraft {
  /** The operator's own words — stored verbatim as the field's label. */
  name: string;
  value: string;
  /** From the shared palette (`@/components/color-picker-popover`). */
  color: string;
}

export type ManualFieldProblem = 'required' | 'invalid';

/** Problems keyed by the input they belong under, so each gets its own error. */
export type ManualFieldDraftErrors = Partial<
  Record<'name' | 'value' | 'color' | 'bbox', ManualFieldProblem>
>;

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

/**
 * Whether a name leaves a key behind.
 *
 * The necessary condition of `ManualFieldKey::fold()`, not a re-implementation
 * of it: the backend owns key generation and its 422 is what protects the data.
 * This only has to separate "you typed nothing usable" from "you typed a name",
 * so it asks the one question the fold can answer negatively — is there a
 * letter or a digit in here at all. Unicode-aware because the documents are
 * Arabic and an ASCII class would reject every real field name.
 */
function hasKeyShape(name: string): boolean {
  return /[\p{L}\p{N}]/u.test(name);
}

/**
 * A drawn box the page can actually hold.
 *
 * Mirrors `CreateManualFieldRequest`'s geometry rules — zero-size refused,
 * nothing past an edge — with the same epsilon, so a value printed flush to the
 * margin is not rejected by a rounding error in the drag maths.
 */
function isDrawable(bbox: UpdateChunkBboxRequest): boolean {
  const epsilon = 1e-6;

  if (![bbox.left, bbox.top, bbox.width, bbox.height].every(Number.isFinite)) {
    return false;
  }

  if (bbox.width <= 0 || bbox.height <= 0) return false;
  if (bbox.left < 0 || bbox.top < 0) return false;

  return (
    bbox.left + bbox.width <= 1 + epsilon &&
    bbox.top + bbox.height <= 1 + epsilon
  );
}

/**
 * What is still wrong with a draft, as field-level problems.
 *
 * Returns a map rather than a boolean so the dialog can put each message under
 * the input it belongs to: one toast saying "check your input" for four inputs
 * spread across a dialog AND the document behind it is not an answer the
 * operator can act on. An empty object means the draft is ready to send.
 */
export function validateManualFieldDraft(
  draft: ManualFieldDraft,
  bbox: UpdateChunkBboxRequest | null
): ManualFieldDraftErrors {
  const errors: ManualFieldDraftErrors = {};

  const name = draft.name.trim();
  if (name === '') {
    errors.name = 'required';
  } else if (!hasKeyShape(name)) {
    errors.name = 'invalid';
  }

  if (draft.value.trim() === '') {
    errors.value = 'required';
  }

  if (draft.color.trim() === '') {
    errors.color = 'required';
  } else if (!HEX_RE.test(draft.color)) {
    errors.color = 'invalid';
  }

  if (bbox == null) {
    errors.bbox = 'required';
  } else if (!isDrawable(bbox)) {
    errors.bbox = 'invalid';
  }

  return errors;
}
